// ===========================================
// EMAIL SERVICE - Production Ready
// ===========================================

import api from './api';

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category: string;
  thumbnail?: string;
  unlayerJson?: Record<string, any> | null;
  preheader?: string;
  replyTo?: string;
  variables: string[];
  usageCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplatesListResponse {
  templates: EmailTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  avgOpenRate?: number;
}

export interface CreateTemplateData {
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category?: string;
  variables?: string[];
  thumbnail?: string;
  unlayerJson?: Record<string, any> | null;
  preheader?: string;
  replyTo?: string;
  destinationRelated?: boolean;
  templateType?: string;
}

export interface EmailSend {
  id: string;
  status: string;
  subject?: string;
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  contact: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  template?: {
    id: string;
    name: string;
  };
  campaign?: {
    id: string;
    name: string;
  };
  createdAt: string;
}

export interface SendsListResponse {
  sends: EmailSend[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const emailService = {
  // Templates
  async listTemplates(params: { page?: number; limit?: number; category?: string; search?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    return api.get<TemplatesListResponse>(`/api/emails/templates?${queryParams.toString()}`);
  },

  async getTemplate(id: string) {
    const response = await api.get<EmailTemplate>(`/api/emails/templates/${id}`);
    // Normalize: if response has a nested template, unwrap it
    return (response as any).template || response;
  },

  async createTemplate(data: CreateTemplateData) {
    const response = await api.post<EmailTemplate>('/api/emails/templates', data);
    // Normalize: if response has a nested template, unwrap it
    return (response as any).template || response;
  },

  async updateTemplate(id: string, data: Partial<CreateTemplateData>) {
    return api.put<EmailTemplate>(`/api/emails/templates/${id}`, data);
  },

  async deleteTemplate(id: string) {
    return api.delete(`/api/emails/templates/${id}`);
  },

  async duplicateTemplate(id: string, newName?: string) {
    return api.post<EmailTemplate>(`/api/emails/templates/${id}/duplicate`, { newName });
  },

  async sendTest(data: {
    templateId?: string;
    subject?: string;
    htmlContent?: string;
    variables?: Record<string, string>;
  }) {
    return api.post<{ recipient: string; messageId?: string }>('/api/emails/send-test', data);
  },

  // Email Sending
  async sendEmail(data: {
    contactId: string;
    templateId: string;
    campaignId?: string;
    variables?: Record<string, string>;
    scheduledAt?: string;
  }) {
    return api.post<EmailSend>('/api/emails/send', data);
  },

  async sendBulk(data: {
    contactIds: string[];
    templateId: string;
    campaignId?: string;
    variables?: Record<string, string>;
    scheduledAt?: string;
  }) {
    return api.post<{ queued: number; failed: number }>('/api/emails/send-bulk', data);
  },

  // Email Sends
  async listSends(params: { page?: number; limit?: number; campaignId?: string; status?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    return api.get<SendsListResponse>(`/api/emails/sends?${queryParams.toString()}`);
  },

  async getSend(id: string) {
    return api.get<EmailSend>(`/api/emails/sends/${id}`);
  },

  async cancelScheduledSend(id: string) {
    return api.delete(`/api/emails/sends/${id}`);
  },

  // Stats
  async getStats() {
    return api.get<{
      totalTemplates: number;
      totalSent: number;
      deliveryRate: number;
      openRate: number;
      clickRate: number;
      recentSends: EmailSend[];
    }>('/api/emails/stats');
  },
};

export default emailService;
